import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Country master (Masters → Countries). The curated, operator-managed country
 * list the app FKs into (distinct from the global `all_countries` reference).
 * See ADR-0016 for the standard master shape.
 */

/** ISO 3166-1 alpha-2, normalized to uppercase (e.g. "IN"). */
export const CountryIso2Schema = z
  .string()
  .trim()
  .regex(/^[A-Za-z]{2}$/, 'Must be a 2-letter ISO code')
  .transform((s) => s.toUpperCase());

/** ISO 3166-1 alpha-3, normalized to uppercase (e.g. "IND"). */
export const CountryIso3Schema = z
  .string()
  .trim()
  .regex(/^[A-Za-z]{3}$/, 'Must be a 3-letter ISO code')
  .transform((s) => s.toUpperCase());

export const CountryListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  isoCode: z.string(),
  isoCode3: z.string().nullable(),
  name: z.string(),
  phoneCode: z.string().nullable(),
  flagEmoji: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  // ----- dr5hn enrichment (ADR-0034) -----
  // Every field nullable AND optional so a v1 API client that didn't ask for
  // them still parses the response (Zod's `.nullable().optional()` accepts
  // undefined, null, or the typed value). Populated by `db:sync:countries`.
  numericCode: z.string().nullable().optional(),
  capital: z.string().nullable().optional(),
  currency: z.string().nullable().optional(),
  currencyName: z.string().nullable().optional(),
  currencySymbol: z.string().nullable().optional(),
  tld: z.string().nullable().optional(),
  native: z.string().nullable().optional(),
  subregion: z.string().nullable().optional(),
  subregionId: z.number().int().nullable().optional(),
  nationality: z.string().nullable().optional(),
  /** Stored as Prisma Decimal; we serialise to string on the wire. */
  latitude: z.string().nullable().optional(),
  longitude: z.string().nullable().optional(),
  emojiU: z.string().nullable().optional(),
  // ----- end ADR-0034 -----
  createdAt: IsoDateTimeSchema,
});
export type CountryListItem = z.infer<typeof CountryListItemSchema>;

export const CountryListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type CountryListQuery = z.infer<typeof CountryListQuerySchema>;

export const CountryListResponseSchema = paginatedResponseSchema(CountryListItemSchema);
export type CountryListResponse = z.infer<typeof CountryListResponseSchema>;

export const CountryDetailSchema = CountryListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type CountryDetail = z.infer<typeof CountryDetailSchema>;

const CountryCoreFieldsSchema = z.object({
  isoCode: CountryIso2Schema,
  isoCode3: CountryIso3Schema.nullable().optional(),
  name: z.string().trim().min(1, 'Name is required').max(120),
  phoneCode: z.string().trim().max(10).nullable().optional(),
  flagEmoji: z.string().trim().max(16).nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
  // NOTE - the dr5hn enrichment fields (ADR-0034: capital, currency,
  // region, …) intentionally stay OUT of create/update. They're managed
  // by the `db:sync:countries` script. When the CMS form grows a UI to
  // edit them, add the fields here AND forward them in
  // `countries.service.ts` create()/update() so we don't silently drop
  // values the operator typed.
});

export const CreateCountryRequestSchema = CountryCoreFieldsSchema;
export type CreateCountryRequest = z.infer<typeof CreateCountryRequestSchema>;

export const UpdateCountryRequestSchema = CountryCoreFieldsSchema.partial();
export type UpdateCountryRequest = z.infer<typeof UpdateCountryRequestSchema>;
